import * as Crypto from "expo-crypto";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as SecureStore from "expo-secure-store";

const getWebCrypto = () => {
  if (Platform.OS !== "web") return null;
  return (globalThis as any)?.crypto?.subtle ?? null;
};

const utf8Encode = (value: string) => {
  if (Platform.OS === "web" && (globalThis as any)?.TextEncoder) {
    return new (globalThis as any).TextEncoder().encode(value);
  }
  return new Uint8Array();
};

const utf8Decode = (value: Uint8Array) => {
  if (Platform.OS === "web" && (globalThis as any)?.TextDecoder) {
    return new (globalThis as any).TextDecoder().decode(value);
  }
  return "";
};

const base64Encode = (value: Uint8Array) => {
  if (Platform.OS === "web" && (globalThis as any)?.btoa) {
    let binary = "";
    value.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return (globalThis as any).btoa(binary);
  }
  return "";
};

const base64Decode = (value?: string) => {
  if (!value) return new Uint8Array();
  if (Platform.OS === "web" && (globalThis as any)?.atob) {
    const binary = (globalThis as any).atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
  return new Uint8Array();
};

export const ENCRYPTION_PREFIX = "enc:v1:";
const LEGACY_MASTER_KEY_STORAGE_KEY = "__vdb_credential_master_key__";
const HARDCODED_MASTER_KEY = base64Decode(
  "m0aP0XK6j6iQ0hVJb9ydFY3OAz3O6B1H2YyR8ZyE8+E="
);
const HKDF_INFO = utf8Encode("vdb-credentials");

const readLegacyKeyFromStorage = async () => {
  if (Platform.OS === "web") {
    return AsyncStorage.getItem(LEGACY_MASTER_KEY_STORAGE_KEY);
  }
  return SecureStore.getItemAsync(LEGACY_MASTER_KEY_STORAGE_KEY);
};

const toUint8Array = (value: Uint8Array | number[]) =>
  value instanceof Uint8Array ? value : new Uint8Array(value);

const getRandomBytes = async (length: number) =>
  toUint8Array(await Crypto.getRandomBytesAsync(length));

const getLegacyMasterKey = async () => {
  const storedKey = await readLegacyKeyFromStorage();
  return storedKey ? base64Decode(storedKey) : null;
};

const deriveKeyWeb = async (
  salt: Uint8Array,
  masterKey: Uint8Array,
  subtle: any
) => {
  const baseKey = await subtle.importKey("raw", masterKey, "HKDF", false, [
    "deriveKey",
  ]);
  return subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt,
      info: HKDF_INFO,
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

const sealWithKey = async (
  plaintext: string,
  salt: Uint8Array,
  nonce: Uint8Array,
  masterKey: Uint8Array
) => {
  const subtle = getWebCrypto();
  if (subtle) {
    const derivedKey = await deriveKeyWeb(salt, masterKey, subtle);
    const sealed = await subtle.encrypt(
      { name: "AES-GCM", iv: nonce },
      derivedKey,
      utf8Encode(plaintext)
    );
    return new Uint8Array(sealed);
  }

  return new Uint8Array();
};

const openWithKey = async (
  salt: Uint8Array,
  nonce: Uint8Array,
  sealed: Uint8Array,
  masterKey: Uint8Array
) => {
  const subtle = getWebCrypto();
  if (subtle) {
    try {
      const derivedKey = await deriveKeyWeb(salt, masterKey, subtle);
      const opened = await subtle.decrypt(
        { name: "AES-GCM", iv: nonce },
        derivedKey,
        sealed
      );
      return new Uint8Array(opened);
    } catch {
      return null;
    }
  }

  return null;
};

export const encryptStringForStorage = async (plaintext: string) => {
  if (!plaintext) return plaintext;
  if (Platform.OS !== "web") return plaintext;

  const salt = await getRandomBytes(16);
  const nonce = await getRandomBytes(12);
  const sealed = await sealWithKey(
    plaintext,
    salt,
    nonce,
    HARDCODED_MASTER_KEY
  );

  const payload = {
    v: 1,
    s: base64Encode(salt),
    n: base64Encode(nonce),
    d: base64Encode(sealed),
  };

  return `${ENCRYPTION_PREFIX}${JSON.stringify(payload)}`;
};

export const decryptStringFromStorage = async (value: string) => {
  if (!value || !value.startsWith(ENCRYPTION_PREFIX)) return value;
  if (Platform.OS !== "web") return "";

  try {
    const payload = JSON.parse(value.slice(ENCRYPTION_PREFIX.length));
    if (!payload?.s || !payload?.n || !payload?.d) return "";
    const salt = base64Decode(payload.s);
    const nonce = base64Decode(payload.n);
    const sealed = base64Decode(payload.d);
    const opened = await openWithKey(salt, nonce, sealed, HARDCODED_MASTER_KEY);

    if (opened) return utf8Decode(opened);

    const legacyKey = await getLegacyMasterKey();
    if (!legacyKey) return "";

    const legacyOpened = await openWithKey(salt, nonce, sealed, legacyKey);

    if (!legacyOpened) return "";
    return utf8Decode(legacyOpened);
  } catch {
    return "";
  }
};

export const removeLegacyMasterKey = async () => {
  if (Platform.OS === "web") {
    return AsyncStorage.removeItem(LEGACY_MASTER_KEY_STORAGE_KEY);
  }
  return SecureStore.deleteItemAsync(LEGACY_MASTER_KEY_STORAGE_KEY);
};
